import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { VaultTopbar } from './components/vault/VaultTopbar'
import { FileTree } from './components/vault/FileTree'
import { ContentPane } from './components/vault/ContentPane'
import { Notification } from './components/Notification'
import { PaywallGate } from './components/vault/PaywallGate'
import { loadQueue, removeFromQueue } from './utils/queueStorage'
import { loadProfile } from './utils/profileStorage'
import { useAuth } from '../auth/AuthContext'
import { scrapeJob, CookieWallError } from './services/jobScraper'
import './ark.css'

// ── Pricing constants (claude-sonnet-4-6) ───────────────────────────────────
const PRICING = {
  input: 3.00 / 1_000_000,
  output: 15.00 / 1_000_000,
  cacheWrite: 3.75 / 1_000_000,
  cacheRead: 0.30 / 1_000_000,
}

function calcCost(usage) {
  if (!usage) return { actual: 0, normal: 0 }
  const inputTokens = usage.input_tokens || 0
  const outputTokens = usage.output_tokens || 0
  const cacheRead = usage.cache_read_input_tokens || 0
  const cacheCreate = usage.cache_creation_input_tokens || 0
  const actual =
    (inputTokens - cacheRead - cacheCreate) * PRICING.input +
    cacheRead * PRICING.cacheRead +
    cacheCreate * PRICING.cacheWrite +
    outputTokens * PRICING.output
  const normal = inputTokens * PRICING.input + outputTokens * PRICING.output
  return { actual, normal }
}

function slug(str) {
  return (str || 'untitled').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60)
}

const SCORE_COLOR = { A: '#7fc97f', B: '#58a6ff', C: '#e8c47f', D: '#e8a07f', F: '#ff8888' }
const SCORE_LABEL = { A: 'Strong Apply', B: 'Apply', C: 'Consider', D: 'Stretch', F: 'Skip' }

// ─────────────────────────────────────────────────────────────────────────────
// Main App
// ─────────────────────────────────────────────────────────────────────────────

export default function ArkApp() {
  const { user } = useAuth()
  const [notification, setNotification] = useState(null)

  // ── Data sources ────────────────────────────────────────────────────────
  const [queueJobs, setQueueJobs] = useState([])
  const [pipelineEntries, setPipelineEntries] = useState([])
  const [stories, setStories] = useState([])

  // ── Selection state ─────────────────────────────────────────────────────
  const [selectedPath, setSelectedPath] = useState(null)
  const [selectedData, setSelectedData] = useState(null)
  const [companyIntel, setCompanyIntel] = useState(null)
  const [jobDescription, setJobDescription] = useState('')
  const [loadingDetail, setLoadingDetail] = useState(false)

  // ── Eval mode (manual URL paste fallback) ───────────────────────────────
  const [evalMode, setEvalMode] = useState(false)
  const [evalFromItem, setEvalFromItem] = useState(null) // item being evaluated

  // ── Token counter ─────────────────────────────────────────────────────
  const [tokenState, setTokenState] = useState(null)

  // ── Load data on mount ────────────────────────────────────────────────
  const refreshData = useCallback(() => {
    setQueueJobs(loadQueue())
    if (user) {
      fetch('/api/ark/pipeline', { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(setPipelineEntries)
        .catch(() => {})
      fetch('/api/ark/stories', { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(setStories)
        .catch(() => {})
    }
  }, [user])

  useEffect(() => { refreshData() }, [refreshData])

  // ── Build virtual file list for FileTree ──────────────────────────────
  const vaultFiles = useMemo(() => {
    const files = []
    // profile.md
    files.push({ path: 'profile.md', frontmatter: { type: 'profile' }, _source: 'profile' })

    // pipeline/ — queue items first, then DB entries (dedup by job_url)
    const seenUrls = new Set()
    for (const q of queueJobs) {
      const key = `pipeline/${slug(q.company_name + '-' + q.job_title)}.md`
      if (q.job_url) seenUrls.add(q.job_url)
      files.push({
        path: key,
        frontmatter: { company: q.company_name, title: q.job_title, status: 'queued', industry: q.industry, work_mode: q.work_mode },
        _source: 'queue', _data: q,
      })
    }
    for (const p of pipelineEntries) {
      if (p.job_url && seenUrls.has(p.job_url)) continue
      const key = `pipeline/${slug(p.company_name + '-' + p.job_title)}.md`
      files.push({
        path: key,
        frontmatter: { company: p.company_name, title: p.job_title, score_letter: p.score_letter, status: p.status },
        _source: 'pipeline', _data: p,
      })
    }

    // stories/
    for (const s of stories) {
      files.push({
        path: `stories/${slug(s.title)}.md`,
        frontmatter: { competency_tags: s.competency_tags },
        _source: 'story', _data: s,
      })
    }

    return files
  }, [queueJobs, pipelineEntries, stories])

  // ── Handle file selection ─────────────────────────────────────────────
  const handleSelect = useCallback(async (path) => {
    setSelectedPath(path)
    setEvalMode(false)
    setEvalFromItem(null)
    setCompanyIntel(null)
    setJobDescription('')

    const file = vaultFiles.find(f => f.path === path)
    if (!file) { setSelectedData(null); return }
    setSelectedData(file)

    // For pipeline items: fetch job description + company intel
    if (file._source === 'queue' || file._source === 'pipeline') {
      setLoadingDetail(true)
      const job = file._data

      // Fetch company intelligence
      if (job.company_name) {
        fetch(`/api/companies/${encodeURIComponent(job.company_name)}/intelligence`)
          .then(r => r.ok ? r.json() : null)
          .then(setCompanyIntel)
          .catch(() => {})
      }

      // Fetch job description from DB first, then Jina fallback
      if (file._source === 'pipeline' && job.job_description) {
        setJobDescription(job.job_description)
        setLoadingDetail(false)
      } else {
        try {
          const params = job.job_url
            ? `url=${encodeURIComponent(job.job_url)}`
            : `company=${encodeURIComponent(job.company_name)}&title=${encodeURIComponent(job.job_title)}`
          const res = await fetch(`/api/jobs/lookup?${params}`)
          if (res.ok) {
            const dbJob = await res.json()
            if (dbJob.job_description) {
              setJobDescription(dbJob.job_description)
              setLoadingDetail(false)
              return
            }
          }
          // Jina fallback
          if (job.job_url) {
            const scraped = await scrapeJob(job.job_url)
            setJobDescription(scraped.description || '')
          }
        } catch {
          // No description available — user can paste manually during eval
        } finally {
          setLoadingDetail(false)
        }
      }
    }
  }, [vaultFiles])

  // ── Start evaluation from a pipeline item ─────────────────────────────
  const handleEvalFromItem = (file) => {
    setEvalFromItem(file)
    setEvalMode(true)
  }

  // ── Manual new evaluation (URL paste) ─────────────────────────────────
  const startManualEval = () => {
    setEvalMode(true)
    setEvalFromItem(null)
    setSelectedPath(null)
    setSelectedData(null)
    setCompanyIntel(null)
    setJobDescription('')
  }

  // ── Handle eval complete ──────────────────────────────────────────────
  const handleEvalComplete = async (result, job) => {
    // Save to ark_pipeline DB
    if (user) {
      try {
        await fetch('/api/ark/pipeline', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            company_name: job.company_name,
            job_title: job.job_title,
            job_url: job.job_url || '',
            job_description: jobDescription,
            score: result.score,
            score_letter: result.score_letter,
            evaluation_json: result,
            status: 'evaluated',
          }),
        })
      } catch (e) { console.error('Failed to save pipeline entry:', e) }

      // Auto-save STAR stories
      if (result.star_stories?.length) {
        for (const story of result.star_stories) {
          try {
            await fetch('/api/ark/stories', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                title: story.title,
                situation: story.situation,
                task: story.task,
                action: story.action,
                result: story.result,
                reflection: story.reflection || '',
                competency_tags: story.competency_tags || [],
              }),
            })
          } catch { /* continue */ }
        }
      }
    }

    // Remove from queue if it came from there
    if (evalFromItem?._source === 'queue' && evalFromItem._data?._key) {
      removeFromQueue(evalFromItem._data._key)
    }

    // Refresh all data
    refreshData()
    setEvalMode(false)
    setEvalFromItem(null)
    setNotification({ message: `Evaluated: ${job.company_name} — ${job.job_title}`, type: 'success' })
  }

  return (
    <div className="ark-vault">
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <VaultTopbar
        onNewEval={startManualEval}
        onLogoClick={() => { setSelectedPath(null); setSelectedData(null); setEvalMode(false) }}
        tokenCounter={tokenState && <TokenCounter state={tokenState} />}
      />

      <div className="ark-vault-body">
        <FileTree
          files={vaultFiles}
          selected={selectedPath}
          onSelect={handleSelect}
        />

        <div className="vault-content">
          {evalMode ? (
            <EvalRunner
              initialJob={evalFromItem?._data || null}
              initialJobDesc={evalFromItem ? jobDescription : ''}
              companyIntel={companyIntel}
              onComplete={handleEvalComplete}
              onCancel={() => { setEvalMode(false); setEvalFromItem(null) }}
              user={user}
              onTokenUpdate={setTokenState}
            />
          ) : (
            <ContentPane
              file={selectedData}
              fileCount={vaultFiles.length}
              companyIntel={companyIntel}
              jobDescription={jobDescription}
              loadingDetail={loadingDetail}
              onNewEval={startManualEval}
              onEvaluate={() => handleEvalFromItem(selectedData)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ── Token counter (topbar) ──────────────────────────────────────────────────

function TokenCounter({ state }) {
  if (!state) return null
  const { streaming, outputTokens, usage } = state

  if (streaming) {
    return (
      <div className="vault-token-counter vault-token-counter--live">
        <span className="vault-token-dot" />
        <span className="vault-token-label">~{outputTokens} tokens</span>
      </div>
    )
  }

  if (usage) {
    const { actual, normal } = calcCost(usage)
    const saved = normal > 0 ? Math.round((1 - actual / normal) * 100) : 0
    return (
      <div className="vault-token-counter vault-token-counter--done">
        <span className="vault-token-label">
          {(usage.input_tokens || 0).toLocaleString()} in / {(usage.output_tokens || 0).toLocaleString()} out
        </span>
        <span className="vault-token-sep">{'\u00B7'}</span>
        <span className="vault-token-cost">
          ${normal.toFixed(3)} {'\u2192'} <strong>${actual.toFixed(3)}</strong>
        </span>
        {saved > 0 && <span className="vault-token-saved">({saved}% saved)</span>}
      </div>
    )
  }

  return null
}

// ── Evaluation runner ───────────────────────────────────────────────────────

function EvalRunner({ initialJob, initialJobDesc, companyIntel, onComplete, onCancel, user, onTokenUpdate }) {
  const [jobUrl, setJobUrl] = useState(initialJob?.job_url || '')
  const [jobDesc, setJobDesc] = useState(initialJobDesc || '')
  const [evalJob, setEvalJob] = useState(initialJob || null)
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState(null)
  const [evaluating, setEvaluating] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)
  const [streamText, setStreamText] = useState('')
  const [usage, setUsage] = useState(null)
  const profile = loadProfile()

  const handleFetch = async () => {
    if (!jobUrl.trim()) { setFetchError('Paste a job URL first.'); return }
    setFetching(true)
    setFetchError(null)
    try {
      // Try DB lookup first
      const lookupRes = await fetch(`/api/jobs/lookup?url=${encodeURIComponent(jobUrl.trim())}`)
      if (lookupRes.ok) {
        const dbJob = await lookupRes.json()
        setEvalJob({ job_url: jobUrl.trim(), job_title: dbJob.job_title, company_name: dbJob.company_name })
        setJobDesc(dbJob.job_description || '')
        setFetching(false)
        return
      }
      // Jina fallback
      const scraped = await scrapeJob(jobUrl.trim())
      setEvalJob({ job_url: jobUrl.trim(), job_title: scraped.title || '', company_name: scraped.company || '' })
      setJobDesc(scraped.description || '')
    } catch (err) {
      if (err instanceof CookieWallError) {
        setEvalJob({ job_url: jobUrl.trim(), job_title: err.title || '', company_name: err.company || '' })
        setFetchError('Cookie wall — paste the job description below.')
      } else {
        setFetchError(err instanceof Error ? err.message : 'Could not fetch that page.')
      }
    } finally {
      setFetching(false)
    }
  }

  const evaluate = async () => {
    const desc = jobDesc || evalJob?.job_description
    if (!desc?.trim()) { setError('Job description required.'); return }
    if (!profile?.name) { setError('Complete your profile first.'); return }
    setEvaluating(true)
    setError(null)
    setStreamText('')
    setUsage(null)
    if (onTokenUpdate) onTokenUpdate({ streaming: true, outputTokens: 0, usage: null })

    try {
      const res = await fetch('/api/ark/evaluate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        credentials: 'include',
        body: JSON.stringify({
          profile,
          jobDescription: desc,
          jobTitle: evalJob?.job_title || '',
          company: evalJob?.company_name || '',
          companyIntelligence: companyIntel || null,
        }),
      })

      if (!res.ok) throw new Error((await res.json()).error || 'Evaluation failed')

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let charCount = 0

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const evt = JSON.parse(line.slice(6))
            if (evt.type === 'delta') {
              charCount += evt.text.length
              setStreamText(prev => prev + evt.text)
              if (onTokenUpdate) onTokenUpdate({ streaming: true, outputTokens: Math.round(charCount / 4), usage: null })
            } else if (evt.type === 'done') {
              setResult(evt.result)
              setUsage(evt.usage)
              if (onTokenUpdate) onTokenUpdate({ streaming: false, outputTokens: evt.usage?.output_tokens || 0, usage: evt.usage })
            } else if (evt.type === 'error') {
              throw new Error(evt.error)
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr
          }
        }
      }
    } catch (e) {
      setError(e.message)
      if (onTokenUpdate) onTokenUpdate(null)
    } finally {
      setEvaluating(false)
    }
  }

  // ── Result view ─────────────────────────────────────────────────────────
  if (result && evalJob) {
    return (
      <div className="vault-eval-done">
        <EvalResultDisplay result={result} costData={usage ? calcCost(usage) : null} />
        <div className="vault-eval-actions">
          <button className="vault-eval-save-btn" onClick={() => onComplete(result, evalJob)}>
            Save to Pipeline
          </button>
          <button className="vault-eval-btn" onClick={() => setResult(null)}>Re-evaluate</button>
          <button className="vault-eval-btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    )
  }

  // ── Input view ──────────────────────────────────────────────────────────
  return (
    <div className="vault-eval-runner">
      <h2 className="vault-eval-title">{initialJob ? `Evaluate: ${initialJob.company_name} — ${initialJob.job_title}` : 'New Evaluation'}</h2>

      {!initialJob && (
        <>
          <p className="vault-eval-desc">Paste a job URL, or go to Open Roles and send jobs here.</p>
          <div className="vault-eval-url-row">
            <input
              className="vault-eval-input"
              placeholder="https://jobs.lever.co/stripe/..."
              value={jobUrl}
              onChange={e => setJobUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleFetch()}
            />
            <button className="vault-eval-fetch-btn" onClick={handleFetch} disabled={fetching}>
              {fetching ? '...' : 'Fetch'}
            </button>
          </div>
          {fetchError && <div className="vault-eval-error">{fetchError}</div>}
          {evalJob && (
            <div className="vault-eval-job-info">
              <span className="vault-eval-check">{'\u2713'}</span>
              <strong>{evalJob.job_title || 'Job'}</strong>
              {evalJob.company_name && <span> at {evalJob.company_name}</span>}
            </div>
          )}
        </>
      )}

      <label className="vault-eval-label">Job Description</label>
      <textarea
        className="vault-eval-textarea"
        placeholder="Paste or edit the job description..."
        value={jobDesc}
        onChange={e => setJobDesc(e.target.value)}
        rows={10}
      />

      {error && <div className="vault-eval-error">{error}</div>}
      {evaluating && streamText && <div className="vault-eval-stream">{streamText}</div>}

      <div className="vault-eval-actions">
        <button className="vault-eval-run-btn" onClick={evaluate} disabled={evaluating || !jobDesc.trim()}>
          {evaluating ? 'Evaluating...' : 'Evaluate Fit \u2192'}
        </button>
        <button className="vault-eval-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

// ── Eval result display ─────────────────────────────────────────────────────

function EvalResultDisplay({ result, costData }) {
  const [unlocked, setUnlocked] = useState(false)

  return (
    <div className="vault-eval-result">
      <div className="vault-eval-score-row">
        <span className="vault-eval-score" style={{ color: SCORE_COLOR[result.score_letter] }}>
          {result.score_letter}
        </span>
        <span className="vault-eval-score-label">{SCORE_LABEL[result.score_letter]}</span>
        <span className="vault-eval-score-num">{result.score}/5.0</span>
      </div>

      <div className="vault-eval-block">
        <h3>Role Summary</h3>
        <p>{result.role_summary}</p>
      </div>

      {!unlocked && <PaywallGate costData={costData} onUnlock={() => setUnlocked(true)} />}

      <div className={!unlocked ? 'vault-eval-locked' : ''}>
        <div className="vault-eval-block">
          <h3>CV Match — {result.cv_match?.match_percent}%</h3>
          <div className="vault-eval-match">
            <div>
              <div className="vault-eval-sublabel">Strengths</div>
              <ul>{(result.cv_match?.strengths || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
            <div>
              <div className="vault-eval-sublabel">Gaps</div>
              <ul className="vault-eval-gaps">{(result.cv_match?.gaps || []).map((g, i) => <li key={i}>{g}</li>)}</ul>
            </div>
          </div>
        </div>

        <div className="vault-eval-block">
          <h3>Comp Research</h3>
          <p>{result.comp_research}</p>
        </div>

        <div className="vault-eval-block">
          <h3>Why You Fit</h3>
          <p>{result.personalization}</p>
        </div>

        <div className="vault-eval-block">
          <h3>Interview Prep</h3>
          {(result.interview_prep || []).map((q, i) => (
            <div key={i} className="vault-eval-interview">
              <div className="vault-eval-question">Q: {q.question}</div>
              <div className="vault-eval-hint">{'\u2192'} {q.story_hint}</div>
            </div>
          ))}
        </div>

        <div className="vault-eval-block">
          <h3>Recommendation</h3>
          <p style={{ fontWeight: 600 }}>{result.recommendation}</p>
        </div>

        {result.star_stories?.length > 0 && (
          <div className="vault-eval-block">
            <h3>STAR Stories ({result.star_stories.length})</h3>
            {result.star_stories.map((s, i) => (
              <div key={i} className="vault-eval-story">
                <div className="vault-eval-story-title">{s.title}</div>
                <div className="vault-eval-story-tags">
                  {(s.competency_tags || []).map(t => <span key={t} className="vault-eval-tag">{t}</span>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
